import * as React from "react";
import { Button, Dropdown, Modal, notification } from "antd";
import { ChevronDown } from "react-feather";
import { request } from "../../services/cpln";
import { Image } from "./../../schema/types/image/entity";
import { Table } from "../../newcomponents/table/table";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { NameColumn } from "../../newcomponents/table/columns/wellKnown/nameColumn";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { getImageTableItem, ImageTableItem } from "../../mst/kinds/image.helpers";
import { CopyButtonWithApproval } from "../domain/create/copyButtonWithApproval";
import { ItemType, MenuItemType } from "antd/es/menu/interface";

interface Props {
  repository: string;
  images: Image[];
  refreshImages: any;
}
export const RepositoryDetail: React.FC<Props> = ({ repository, images, refreshImages }) => {
  const org = useAppSelector(selectOrg);
  const registryEndpoint = useAppSelector((state) => state.discovery.endpoints.registry);
  const [isLoadingDelete, setIsLoadingDelete] = React.useState(false);
  const [selections, setSelections] = React.useState<string[]>([]);
  const [isDeletingImages, setIsDeletingImages] = React.useState(false);

  function pullImage(image: string) {
    const pullCommand = `docker pull ${registryEndpoint?.replace("https://", "").replace("{org}", org!)}/${image}`;

    Modal.info({
      width: 700,
      title: `Docker Pull Command`,
      maskClosable: true,
      content: (
        <div>
          <div className="flex items-center">
            <input
              data-testid="step-pull"
              className="flex-grow inline-flex px-4 py-2 border"
              style={{
                width: 450,
              }}
              disabled={true}
              value={pullCommand}
            />
            <CopyButtonWithApproval className="ml-1" value={pullCommand} />
          </div>
        </div>
      ),
    });
  }

  async function confirmImagesDeletion() {
    try {
      setIsLoadingDelete(true);
      const promises: Promise<any>[] = [];
      for (let imageLink of selections) {
        promises.push(request({ url: imageLink, method: "delete" }));
      }
      await Promise.all(promises);
      notification.success({ message: "Success", description: "Deleted images" });
    } catch (e) {
      notification.success({ message: "Failed", description: "Failed to delete all images" });
    } finally {
      setSelections([]);
      refreshImages();
      setIsLoadingDelete(false);
      setIsDeletingImages(false);
    }
  }

  function cancelImagesDeletion() {
    setIsLoadingDelete(false);
    setIsDeletingImages(false);
  }

  const imageTableItems: ImageTableItem[] = images.map((item) => getImageTableItem({ item: item }));

  return (
    <>
      <div className="n-detail">
        <div className="n-detail-header border-b flex items-center justify-between pb-4 h-20 z-10 mb-4">
          <div>
            <div data-testid="detail-kind" className="text-dark text-xs">
              IMAGE
            </div>
            <div className="flex items-center">
              <div data-testid="detail-name" className="text-2xl font-semibold leading-none">
                {repository}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "delete-images",
                    label: "Delete Images",
                    danger: true,
                    disabled: selections.length < 1,
                  },
                ] as ItemType<MenuItemType>[],
                onClick: ({ key }) => {
                  if (key === "delete-images") {
                    setIsDeletingImages(true);
                  }
                },
              }}
            >
              <Button type={"primary"} size="small" icon={<ChevronDown />} iconPosition="end">
                Actions
              </Button>
            </Dropdown>
          </div>
        </div>
        <div className="n-detail-main">
          <Table<ImageTableItem>
            tableId="repository-detail"
            hideSettings={true}
            enableFilter
            data={imageTableItems}
            selectMode="multi"
            selectKey="selfLink"
            selections={selections}
            onSelectionsChange={setSelections}
            columns={[
              NameColumn(),
              TagsColumn(),
              CreatedColumn(),
              LastModifiedColumn(),
              {
                id: "actions",
                label: "",
                cell: (props) => {
                  const image = props.row.original;
                  return (
                    <div className="flex items-center justify-end w-full">
                      <div className="flex items-center" style={{ minWidth: 50 }}>
                        <Button
                          style={{ minWidth: 50 }}
                          size={"small"}
                          onClick={() => pullImage(image.name)}
                          type={"primary"}
                        >
                          <span style={{ lineHeight: "1.7" }}>Pull</span>
                        </Button>
                      </div>
                    </div>
                  );
                },
              },
            ]}
          />
        </div>
      </div>
      {isDeletingImages ? (
        <Modal
          title="Delete Images"
          open={true}
          maskClosable={!isLoadingDelete}
          destroyOnHidden
          onCancel={cancelImagesDeletion}
          onOk={confirmImagesDeletion}
          cancelText={`Cancel`}
          okText={`Delete`}
          cancelButtonProps={{ type: "default", disabled: isLoadingDelete }}
          okButtonProps={{ type: "primary", danger: true, disabled: isLoadingDelete, loading: isLoadingDelete }}
        >
          Are you sure you want to delete the selected images?
        </Modal>
      ) : null}
    </>
  );
};
